import Nav from "@/components/Nav";
import PageHero from "@/components/PageHero";
import MarketingFooter from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { ShieldCheck, BrainCircuit, Sparkles, FileText, ArrowRight, Check } from "lucide-react";
import { usePageMeta } from "@/lib/pageMeta";
import { motion } from "framer-motion";

const STEPS = [
  {
    n: "01",
    icon: ShieldCheck,
    title: "Verified You",
    tagline: "Trust begins with truth.",
    body: "Sign up in under 90 seconds. Complete your biodata — education, career, family, values, lifestyle. Verify identity with ID + selfie KYC. Every profile you'll see on Anubandh.ai is a verified human, not a farmed listing. No fake profiles. No pretend accounts.",
    outputs: ["KYC-verified badge on your profile", "Structured biodata elders can trust", "Encrypted document storage"],
  },
  {
    n: "02",
    icon: BrainCircuit,
    title: "Marriage Intelligence Assessment",
    tagline: "18 questions. 8 dimensions. 12 quiet minutes.",
    body: "The Assessment is where destiny meets data. Eighteen carefully designed questions — grounded in relationship psychology, adult attachment theory, and Indian family systems — read across eight dimensions that predict long-term marital fulfilment. This isn't a personality test. It's a mirror.",
    outputs: ["Personality & emotional intelligence signal", "Communication style profile", "Family-values alignment vector", "Financial & life-priority orientation"],
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Fusion Report",
    tagline: "Kundali × AI, in one clear report.",
    body: "For every curated match, we generate a Marriage Intelligence Report — Vedic Ashtakoot Guna Milan (36 gunas) fused with modern AI compatibility across your 8 dimensions. Each report is explainable: you see the strengths, the friction, and the exact conversations to have before you say yes.",
    outputs: ["Fusion Score (Vedic + AI)", "Dimension radar chart", "Shared strengths + potential friction", "Recommended pre-marriage conversations"],
  },
  {
    n: "04",
    icon: FileText,
    title: "Shared Report",
    tagline: "The decision is yours. The blessing is theirs.",
    body: "Download your Marriage Intelligence Report as a PDF. Share with parents, elders, or trusted mentors. Journey (Elite) users can also invite up to 4 family members to a permissioned Family Circle where reports are visible to family — with revocable access. Family becomes a first-class user, not an afterthought.",
    outputs: ["Downloadable PDF report", "Public share link (view-only)", "Family Circle for 4 members (Journey tier)", "Guided pre-marriage conversation prompts"],
  },
];

export default function HowItWorks() {
  usePageMeta(
    "How it works",
    "From adaptive assessment to explainable AI compatibility report — the four-step Marriage Intelligence journey by Anubandh.ai."
  );
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav variant="marketing" />
      <PageHero
        overline="The Marriage Intelligence Method"
        sanskrit="बुद्धि · पद्धति · स्पष्टता"
        title={<>Four thoughtful steps<br /><span className="italic text-amber-300">to a decision you can stand behind.</span></>}
        subtitle="No swiping. No endless browsing. No pressure. Every step at Anubandh.ai is designed for the seriousness of a lifelong decision — and the peace of a quiet home."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/signup" data-testid="how-signup-cta" className="btn-pill bg-amber-500 text-[#12103A] text-sm px-6 py-3 font-medium inline-flex items-center gap-2">
            Begin Your Assessment <ArrowRight size={16} />
          </Link>
          <Link to="/kundali" className="btn-pill border border-white/25 text-white text-sm px-6 py-3 hover:bg-white/10">See Kundali × AI</Link>
        </div>
      </PageHero>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="space-y-16">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start"
            >
              <div className="lg:col-span-4">
                <div className="font-serif-display text-6xl lg:text-7xl text-amber-600/70 leading-none">{s.n}</div>
                <div className="mt-4 inline-flex items-center gap-2 text-amber-700">
                  <s.icon size={20} />
                  <div className="overline">{`Step ${i + 1}`}</div>
                </div>
                <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1E1B4B] mt-3 leading-tight">{s.title}</h2>
                <p className="mt-2 text-amber-700 italic text-sm">{s.tagline}</p>
              </div>
              <div className="lg:col-span-8 border border-stone-200 rounded-2xl bg-white p-8 lg:p-10">
                <p className="text-stone-700 leading-relaxed">{s.body}</p>
                <div className="mt-6 pt-6 border-t border-stone-100">
                  <div className="overline mb-3">What you get</div>
                  <ul className="space-y-2">
                    {s.outputs.map((o) => (
                      <li key={o} className="flex items-start gap-2 text-sm text-stone-700">
                        <Check size={16} className="text-amber-700 mt-0.5 shrink-0" />{o}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-white border-t border-stone-200">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="overline">Ready when you are</div>
          <h3 className="font-serif-display text-3xl lg:text-4xl text-[#1E1B4B] mt-3 leading-tight">
            One decision. Done with clarity.
          </h3>
          <p className="mt-4 text-stone-600 max-w-xl mx-auto">
            The Assessment is free. The first report is free. Upgrade only when you&apos;re ready — never before.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup" data-testid="how-final-cta" className="btn-pill text-white px-7 py-3.5 text-sm" style={{ background: "#1E1B4B" }}>
              Start free
            </Link>
            <Link to="/subscription" className="btn-pill border border-stone-300 text-[#1E1B4B] px-7 py-3.5 text-sm hover:bg-stone-50">See plans</Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
